import { useState, useEffect, useRef, useCallback } from 'react';

export interface SlideImage {
  src: string;
  alt: string;
}

interface PhotoSlideshowProps {
  images: SlideImage[];
  autoPlayInterval?: number;
  aspectRatio?: string;
  className?: string;
  overlayDark?: boolean;
  transition?: 'fade' | 'slide';
}

export default function PhotoSlideshow({
  images,
  autoPlayInterval = 4200,
  aspectRatio = '16/7',
  className = '',
  overlayDark = false,
  transition = 'fade',
}: PhotoSlideshowProps) {
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const touchStartX = useRef<number | null>(null);

  const next = useCallback(() => {
    setCurrent((c) => (c + 1) % images.length);
  }, [images.length]);

  const prev = useCallback(() => {
    setCurrent((c) => (c - 1 + images.length) % images.length);
  }, [images.length]);

  useEffect(() => {
    if (!isPaused) {
      timerRef.current = setInterval(next, autoPlayInterval);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isPaused, next, autoPlayInterval]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 48) {
      diff > 0 ? next() : prev();
    }
    touchStartX.current = null;
  };

  const slideUnit = 100 / images.length;

  return (
    <div
      className={`relative overflow-hidden rounded-2xl ${className}`}
      style={{ aspectRatio }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {transition === 'slide' ? (
        /* ── Horizontal slide strip ── */
        <div
          className="absolute inset-0 flex"
          style={{
            width: `${images.length * 100}%`,
            transform: `translateX(-${current * slideUnit}%)`,
            transition: 'transform 700ms cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {images.map((img, i) => (
            <div
              key={i}
              className="relative h-full flex-shrink-0"
              style={{ width: `${slideUnit}%` }}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover object-center"
              />
              {overlayDark && (
                <div className="absolute inset-0 bg-[#1a1a3e]/15"></div>
              )}
            </div>
          ))}
        </div>
      ) : (
        /* ── Crossfade ── */
        images.map((img, i) => (
          <div
            key={i}
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
              i === current ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={img.src}
              alt={img.alt}
              className="w-full h-full object-cover object-top"
            />
            {overlayDark && (
              <div className="absolute inset-0 bg-[#1a1a3e]/15"></div>
            )}
          </div>
        ))
      )}

      {/* Prev Button */}
      <button
        onClick={prev}
        className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-black/20 text-white hover:bg-black/40 transition-all duration-200 cursor-pointer"
        aria-label="前の写真"
      >
        <i className="ri-arrow-left-s-line text-lg"></i>
      </button>

      {/* Next Button */}
      <button
        onClick={next}
        className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-black/20 text-white hover:bg-black/40 transition-all duration-200 cursor-pointer"
        aria-label="次の写真"
      >
        <i className="ri-arrow-right-s-line text-lg"></i>
      </button>

      {/* Dot Indicators */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 items-center">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
              i === current ? 'bg-white w-5' : 'bg-white/50 w-1.5'
            }`}
            aria-label={`写真 ${i + 1}`}
          />
        ))}
      </div>

      {/* Progress Bar */}
      {!isPaused && (
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/20">
          <div
            key={current}
            className="h-full bg-white/60"
            style={{
              animation: `slideProgress ${autoPlayInterval}ms linear forwards`,
            }}
          />
        </div>
      )}

      <style>{`
        @keyframes slideProgress {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </div>
  );
}
